
import {
  computed, defineComponent, PropType, reactive, watch,
} from 'vue';
import { Modal } from 'ant-design-vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import Icone from '@/core/components/Icone.vue';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { ICteDocumentoReferenciado } from '@/models/Entidades/Compras/ConhecimentoTransporte/ICteDocumentoReferenciado';
import CteDocumentoReferenciadoModal from './CteDocumentoReferenciadoModal.vue';

export default defineComponent({
  name: 'CteDocumentos',
  components: {
    CampoNumerico,
    MensagemSemDados,
    Icone,
    CteDocumentoReferenciadoModal,
  },
  props: {
    documentos: {
      type: Array as PropType<ICteDocumentoReferenciado[]>,
      default: () => [],
    },
    valorFrete: {
      type: Number,
      default: 0,
    },
    editavel: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:documentos'],
  setup(props, { emit }) {
    const state = reactive({
      exibirModal: false,
      documentoSelecionado: null as ICteDocumentoReferenciado | null,
      indexSelecionado: -1,
    });

    const colunas = [
      {
        title: 'Chave de Acesso',
        dataIndex: 'chaveAcessoDocumento',
        key: 'chaveAcessoDocumento',
        ellipsis: true,
      },
      {
        title: 'Cód. NF Entrada',
        dataIndex: 'codigoNotaFiscalEntrada',
        key: 'codigoNotaFiscalEntrada',
        width: 130,
        align: 'center',
      },
      {
        title: 'Valor Documento',
        dataIndex: 'valorDocumento',
        key: 'valorDocumento',
        width: 160,
        align: 'right',
      },
      {
        title: 'Peso (kg)',
        dataIndex: 'pesoDocumento',
        key: 'pesoDocumento',
        width: 100,
        align: 'right',
      },
      {
        title: 'Frete por Doc.',
        dataIndex: 'valorFretePorDocumento',
        key: 'valorFretePorDocumento',
        width: 140,
        align: 'right',
      },
      {
        title: 'Ações',
        key: 'acoes',
        width: 70,
        align: 'center',
      },
    ];

    const totalDocumentos = computed(() => props.documentos.reduce((acc, d) => acc + (d.valorDocumento || 0), 0));

    function recalcularFreteProporcional(docs: ICteDocumentoReferenciado[], frete: number): ICteDocumentoReferenciado[] {
      // Verifica se todos os documentos têm peso preenchido (> 0)
      const todosComPeso = docs.every((d) => (d.pesoDocumento ?? 0) > 0);

      if (todosComPeso) {
        // Rateio pelo peso
        const totalPeso = docs.reduce((acc, d) => acc + (d.pesoDocumento || 0), 0);
        return docs.map((d) => ({
          ...d,
          valorFretePorDocumento: totalPeso > 0 ? ((d.pesoDocumento || 0) / totalPeso) * frete : 0,
        }));
      }

      // Rateio pelo valor (comportamento anterior)
      const totalValor = docs.reduce((acc, d) => acc + (d.valorDocumento || 0), 0);
      return docs.map((d) => ({
        ...d,
        valorFretePorDocumento: totalValor > 0 ? (d.valorDocumento / totalValor) * frete : 0,
      }));
    }

    watch(() => props.valorFrete, (novoFrete) => {
      if (!props.documentos || props.documentos.length === 0) return;
      const totalValor = props.documentos.reduce((acc, d) => acc + (d.valorDocumento || 0), 0);
      if (totalValor === 0) return;
      emit('update:documentos', recalcularFreteProporcional(props.documentos, novoFrete));
    });

    function adicionarDocumento() {
      state.indexSelecionado = -1;
      state.documentoSelecionado = null;
      state.exibirModal = true;
    }

    function editarDocumento(index: number) {
      state.indexSelecionado = index;
      state.documentoSelecionado = { ...props.documentos[index] };
      state.exibirModal = true;
    }

    function excluirDocumento(index: number) {
      Modal.confirm({
        title: 'Excluir documento referenciado',
        content: 'Deseja realmente excluir este documento?',
        okText: 'Sim',
        cancelText: 'Não',
        onOk: () => {
          const docs = props.documentos.filter((_, i) => i !== index);
          emit('update:documentos', recalcularFreteProporcional(docs, props.valorFrete));
        },
      });
    }

    function confirmarDocumento(documento: ICteDocumentoReferenciado, indexEdicao: number) {
      let docs: ICteDocumentoReferenciado[];
      if (indexEdicao >= 0) {
        docs = props.documentos.map((d, i) => (i === indexEdicao ? { ...documento } : { ...d }));
      } else {
        docs = [...props.documentos, { ...documento }];
      }
      emit('update:documentos', recalcularFreteProporcional(docs, props.valorFrete));
    }

    return {
      props,
      state,
      colunas,
      totalDocumentos,
      UtilitarioMascara,
      adicionarDocumento,
      editarDocumento,
      excluirDocumento,
      confirmarDocumento,
    };
  },
});
